import React from 'react';
import PropTypes from 'prop-types';
import { Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectChangeOrderDetails } from '../../redux/selectors';

const ChangeOrderNotice = ({ jobId, job }) => {
  // Get the change order details from Redux
  const changeOrderDetails = useSelector(selectChangeOrderDetails);

  if (!changeOrderDetails || changeOrderDetails.length === 0) {
    return null;
  }

  // Filter change order details for this job
  const jobChangeOrders = changeOrderDetails.filter(
    (detail) => String(detail.ProjectID) === String(jobId)
  );

  // Group incomplete tasks by engineer type
  const incompleteTasks = {
    mechanical: [],
    electrical: [],
    controls: [],
    csm: []
  };

  jobChangeOrders.forEach((detail) => {
    if (detail.ActionRequiredMechanical && !detail.MechanicalCompleted) {
      incompleteTasks.mechanical.push(detail);
    }
    if (detail.ActionRequiredElectrical && !detail.ElectricalCompleted) {
      incompleteTasks.electrical.push(detail);
    }
    if (detail.ActionRequiredControls && !detail.ControlsCompleted) {
      incompleteTasks.controls.push(detail);
    }
    if (detail.ActionRequiredCSM && !detail.CSMCompleted) {
      incompleteTasks.csm.push(detail);
    }
  });

  // Calculate total incomplete tasks
  const totalIncompleteTasks = 
    incompleteTasks.mechanical.length +
    incompleteTasks.electrical.length +
    incompleteTasks.controls.length +
    incompleteTasks.csm.length;

  // If no incomplete tasks, don't render
  if (totalIncompleteTasks === 0) {
    return null;
  }

  // Get engineer names from job details
  const mechanicalEngineer = job?.MechanicalEngineer || 'Not Assigned';
  const electricalEngineer = job?.ElectricalEngineer || 'Not Assigned';
  const controlsEngineer = job?.ControlsEngineer || 'Not Assigned';

  // Create the tooltip component with detailed task information
  const renderTooltip = (props) => (
    <Tooltip id="change-order-tooltip" {...props}>
      <div style={{ whiteSpace: 'pre-line', textAlign: 'left' }}>
        <strong>Incomplete Change Order Tasks:</strong>
        <br />
        {incompleteTasks.mechanical.length > 0 && (
          <>
            Mechanical: {incompleteTasks.mechanical.length} task{incompleteTasks.mechanical.length !== 1 ? 's' : ''} - {mechanicalEngineer}
            <br />
          </>
        )}
        {incompleteTasks.electrical.length > 0 && (
          <>
            Electrical: {incompleteTasks.electrical.length} task{incompleteTasks.electrical.length !== 1 ? 's' : ''} - {electricalEngineer}
            <br />
          </>
        )}
        {incompleteTasks.controls.length > 0 && (
          <>
            Controls: {incompleteTasks.controls.length} task{incompleteTasks.controls.length !== 1 ? 's' : ''} - {controlsEngineer}
            <br />
          </>
        )}
        {incompleteTasks.csm.length > 0 && (
          <>
            CSM: {incompleteTasks.csm.length} task{incompleteTasks.csm.length !== 1 ? 's' : ''} - David Neesen
            <br />
          </>
        )}
      </div>
    </Tooltip>
  );

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={renderTooltip}
    >
      <Alert variant="warning" className="mb-3 d-flex align-items-center">
        <i className="bi bi-clipboard-check me-2"></i>
        <span>This job has <strong>{totalIncompleteTasks}</strong> open change order task{totalIncompleteTasks !== 1 ? 's' : ''}</span>
      </Alert>
    </OverlayTrigger>
  );
};

ChangeOrderNotice.propTypes = {
  jobId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]).isRequired,
  job: PropTypes.object
};

export default ChangeOrderNotice;
